import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDatepickerModule,
  MatDateRangeInput,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IdNameModel } from '../../../core/models/IdNameModel';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';

export interface MaintenanceFiltersDialogData {
  equipmentMachineId: number | null;
  locationId: number | null;
  maintenanceCauseId: number | null;
  maintenanceSubCauseId: number | null;

  statuses: string | null;
  priority: string | null;

  isOperational: boolean | null;
  isTaggedOut: boolean | null;
  isLOTOFollowed: boolean | null;

  employeeId: number | null;
  assignedToId: number | null;

  priorities: string[];
  statusesOptions: string[];

  maintenanceCauses: IdNameModel[];
  maintenanceSubCauses: IdNameModel[];

  equipmentMachines: IdNameModel[];
  locations: IdNameModel[];
  startDate: Date | null;
  endDate: Date | null;
}

export interface MaintenanceFiltersDialogResult {
  equipmentMachineId?: number;
  locationId?: number;
  maintenanceCauseId?: number;
  maintenanceSubCauseId?: number;

  statuses?: string[];
  priority?: string;
  employeeId?: number;

  isOperational?: boolean;
  isTaggedOut?: boolean;
  isLOTOFollowed?: boolean;

  startDate?: Date | null;
  endDate?: Date | null;

  assignedToId?: number;
}

@Component({
  selector: 'app-maintenance-filters-dialog',
  imports: [
    MatButton,
    FormsModule,
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatDateRangeInput,
    MatDialogActions,
    MatSelectModule,
    MatDialogContent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
  ],
  templateUrl: './maintenance-filters-dialog.html',
  styleUrl: './maintenance-filters-dialog.scss',
})
export class MaintenanceFiltersDialog {
  private dialogRef = inject(MatDialogRef<MaintenanceFiltersDialog>);
  data = inject(MAT_DIALOG_DATA) as MaintenanceFiltersDialogData;
  fb = inject(FormBuilder);

  form: FormGroup = this.fb.group({
    equipmentMachineId: [this.data.equipmentMachineId ?? null],
    locationId: [this.data.locationId ?? null],
    maintenanceCauseId: [this.data.maintenanceCauseId ?? null],
    maintenanceSubCauseId: [this.data.maintenanceSubCauseId ?? null],

    statuses: [this.data.statuses ?? []],
    priority: [this.data.priority ?? null],

    isOperational: [
      this.data.isOperational === true
        ? true
        : this.data.isOperational === false
          ? false
          : null,
    ],
    isTaggedOut: [
      this.data.isTaggedOut === true
        ? true
        : this.data.isTaggedOut === false
          ? false
          : null,
    ],
    isLOTOFollowed: [
      this.data.isLOTOFollowed === true
        ? true
        : this.data.isLOTOFollowed === false
          ? false
          : null,
    ],

    assignedToId: [this.data.assignedToId ?? null],

    startDate: [null],
    endDate: [null],
  });

  ngOnInit() {
    // Initialize startDate and endDate if they are provided in the data
    if (this.data.startDate) {
      this.form.patchValue({ startDate: new Date(this.data.startDate) });
    }

    if (this.data.endDate) {
      this.form.patchValue({ endDate: new Date(this.data.endDate) });
    }

    if (this.data.equipmentMachineId) {
      this.form.patchValue({
        equipmentMachineId: this.data.equipmentMachineId,
      });
    }

    if (this.data.locationId) {
      this.form.patchValue({ locationId: this.data.locationId });
    }

    if (this.data.maintenanceCauseId) {
      this.form.patchValue({
        maintenanceCauseId: this.data.maintenanceCauseId,
      });
    }

    if (this.data.maintenanceSubCauseId) {
      this.form.patchValue({
        maintenanceSubCauseId: this.data.maintenanceSubCauseId,
      });
    }

    if (this.data.statuses) {
      this.form.patchValue({ statuses: this.data.statuses.split(',') });
    }
    if (this.data.priority) {
      this.form.patchValue({ priority: this.data.priority });
    }
    if (this.data.isOperational !== null) {
      this.form.patchValue({ isOperational: this.data.isOperational });
    }
    if (this.data.isTaggedOut !== null) {
      this.form.patchValue({ isTaggedOut: this.data.isTaggedOut });
    }
    if (this.data.isLOTOFollowed !== null) {
      this.form.patchValue({ isLOTOFollowed: this.data.isLOTOFollowed });
    }
  }

  apply() {
    const v = this.form.value;

    const result: MaintenanceFiltersDialogResult = {
      equipmentMachineId: v.equipmentMachineId ?? undefined,
      locationId: v.locationId ?? undefined,
      maintenanceCauseId: v.maintenanceCauseId ?? undefined,
      maintenanceSubCauseId: v.maintenanceSubCauseId ?? undefined,

      statuses: v.statuses ?? [],
      priority: v.priority ?? undefined,

      isOperational: v.isOperational ?? undefined,
      isTaggedOut: v.isTaggedOut ?? undefined,
      isLOTOFollowed: v.isLOTOFollowed ?? undefined,

      startDate: v.startDate ?? null,
      endDate: v.endDate ?? null,

      assignedToId: v.assignedToId ?? undefined,

      employeeId: undefined, // included because it's in interface
    };

    this.dialogRef.close(result);
  }

  clear() {
    this.form.reset({
      equipmentMachineId: null,
      locationId: null,
      maintenanceCauseId: null,
      maintenanceSubCauseId: null,
      statuses: [],
      priority: null,
      isOperational: null,
      isTaggedOut: null,
      isLOTOFollowed: null,
      assignedToId: null,
      startDate: null,
      endDate: null,
    });

    const result: MaintenanceFiltersDialogResult = {
      equipmentMachineId: undefined,
      locationId: undefined,
      maintenanceCauseId: undefined,
      maintenanceSubCauseId: undefined,

      statuses: [],
      priority: undefined,

      isOperational: undefined,
      isTaggedOut: undefined,
      isLOTOFollowed: undefined,

      startDate: null,
      endDate: null,

      assignedToId: undefined,
      employeeId: undefined,
    };

    this.dialogRef.close(result);
  }

  close() {
    this.dialogRef.close();
  }
}
